import { type CSSProperties, useCallback, useEffect, useRef, useState } from "react";

const FADE = "2rem";

/**
 * Fades the edge of a scroll box on each side where the content continues. A mask fades the content on any
 * background; an overlay gradient must know the background color, and a gray weekend row would show the overlay.
 * The box has a fixed height, so a resize observer on the box alone does not see a change of its content. The hook
 * observes the first child too: put all content in one inner element. The `scroll` event covers `scrollIntoView`.
 * The caller sets `scroll-py-8` (2rem, the size of the fade) on the box, so `scrollIntoView` keeps an entry clear of the fade.
 */
export function useScrollFade<T extends HTMLElement>() {
	const ref = useRef<T>(null);
	const [edges, setEdges] = useState({ up: false, down: false });

	const update = useCallback(() => {
		const box = ref.current;
		if (!box) return;
		const up = box.scrollTop > 0;
		const down = box.scrollTop + box.clientHeight < box.scrollHeight - 1;
		setEdges((prev) => (prev.up === up && prev.down === down ? prev : { up, down }));
	}, []);

	useEffect(() => {
		const box = ref.current;
		if (!box) return;
		update();
		box.addEventListener("scroll", update, { passive: true });
		const observer = new ResizeObserver(update);
		observer.observe(box);
		if (box.firstElementChild) observer.observe(box.firstElementChild);
		return () => {
			box.removeEventListener("scroll", update);
			observer.disconnect();
		};
	}, [update]);

	let style: CSSProperties | undefined;
	if (edges.up || edges.down) {
		const gradient = `linear-gradient(to bottom, ${edges.up ? "transparent" : "black"}, black ${edges.up ? FADE : "0"}, black calc(100% - ${edges.down ? FADE : "0px"}), ${edges.down ? "transparent" : "black"})`;
		style = { maskImage: gradient, WebkitMaskImage: gradient };
	}

	return { ref, style };
}
